import { Building2, Check } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { fetchMyTenants } from '@/api/identity'
import { switchTenant } from '@/lib/tenantSwitch'

/**
 * The organisations this person may act in, inside the user menu.
 *
 * <b>It renders nothing at all for almost everybody.</b> Most people belong to one tenant, and a switcher
 * offering a single option is a control that cannot do anything — so the whole section is absent unless there
 * is a second organisation to move to. That is also why this is a fragment of the user menu rather than a
 * permanent fixture in the top bar: for one-tenant installations there is nothing to show.
 *
 * <b>Why a full sign-in and not a quieter update.</b> A token names exactly one tenant, so moving means
 * getting another token, which means a redirect through the IdP. `switchTenant` tears down the live socket
 * and the query cache on the way out — see that module for why neither can be left to the navigation.
 */
export function TenantSwitcher() {
  // Failure is silence here. The switcher is a convenience inside a menu whose other items must keep
  // working, and an error banner over "we could not list your other organisations" would be louder than the
  // thing it describes. A person who cannot be shown the list can still reach a tenant by its own /t/{slug}.
  const { data } = useQuery({
    queryKey: ['my-tenants'],
    queryFn: fetchMyTenants,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const tenants = data?.tenants ?? []
  if (tenants.length < 2) {
    return null
  }

  return (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        Organisation
      </DropdownMenuLabel>
      {tenants.map((tenant) => (
        <DropdownMenuItem
          key={tenant.id}
          disabled={tenant.isCurrent}
          onClick={() => {
            if (!tenant.isCurrent) {
              void switchTenant(tenant.slug, window.location.hash || '#/')
            }
          }}
        >
          {tenant.isCurrent ? (
            <Check className="mr-2 h-4 w-4" />
          ) : (
            <Building2 className="mr-2 h-4 w-4" />
          )}
          <span className="truncate">{tenant.displayName}</span>
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
    </>
  )
}
